import H from '@here/maps-api-for-javascript';
import { AuthError } from '@azure/msal-browser';
import { CompoundBounds, CompoundMetadata } from '@/types/CompoundMap';
import {
  compoundMap,
  CompoundRequestError,
} from '@/api/CommandCentre/CompoundMap';
import { removeCadPaper } from '@/modules/commandCentre/compound/spatial';
import {
  cadTileKey,
  CadTileCoordinate,
  CompoundCadTileCache,
  isFatalCadError,
} from '@/modules/commandCentre/compound/CompoundCadTileCache';

const longitudeX = (longitude: number) => (longitude + 180) / 360;
const latitudeY = (latitude: number) => {
  const radians =
    (Math.max(-85.0511287798066, Math.min(85.0511287798066, latitude)) *
      Math.PI) /
    180;
  return (1 - Math.asinh(Math.tan(radians)) / Math.PI) / 2;
};
const tileRange = (bounds: CompoundBounds, z: number) => {
  const scale = 2 ** z;
  return {
    left: Math.max(0, Math.floor(longitudeX(bounds.west) * scale)),
    right: Math.min(scale - 1, Math.ceil(longitudeX(bounds.east) * scale) - 1),
    top: Math.max(0, Math.floor(latitudeY(bounds.north) * scale)),
    bottom: Math.min(scale - 1, Math.ceil(latitudeY(bounds.south) * scale) - 1),
  };
};
const tilesOverlap = (tile: CadTileCoordinate, bounds: CompoundBounds) => {
  const range = tileRange(bounds, tile.z);
  return (
    tile.x >= range.left &&
    tile.x <= range.right &&
    tile.y >= range.top &&
    tile.y <= range.bottom
  );
};

export class CompoundCadLayer {
  readonly ready: Promise<void>;
  private readonly cache: CompoundCadTileCache;
  private readonly overview = new Map<
    string,
    { tile: CadTileCoordinate; canvas: HTMLCanvasElement }
  >();
  private readonly empty: HTMLCanvasElement;
  private provider?: H.map.provider.ImageTileProvider;
  private layer?: H.map.layer.TileLayer;
  private disposed = false;
  private decodedTiles = 0;
  private readonly attempted = new Set<string>();
  private refreshTimer?: ReturnType<typeof setTimeout>;
  private softReloads = 0;
  private dirty = false;
  private interacting = false;
  private cameraChanged = false;
  private viewKey = '';
  private pruneTimer?: ReturnType<typeof setTimeout>;
  private readonly listeners: Array<[string, () => void]> = [];

  constructor(
    private readonly map: H.Map,
    private readonly metadata: CompoundMetadata,
    private readonly linework: boolean,
    private opacity: number,
    private readonly onError: (error: Error) => void,
  ) {
    this.empty = document.createElement('canvas');
    this.empty.width = this.empty.height = 1;
    this.cache = new CompoundCadTileCache(
      (tile, signal) => this.decode(tile, signal),
      {
        onError: (error) => this.fail(error),
        onLoaded: (_tile, pinned) => {
          if (!pinned) this.scheduleRefresh();
        },
      },
    );
    this.viewKey = this.cameraKey();
    this.listen('mapviewchangestart', () => {
      this.interacting = true;
      this.cameraChanged = false;
      clearTimeout(this.refreshTimer);
      this.refreshTimer = undefined;
    });
    this.listen('mapviewchange', () => {
      this.observeCamera();
      if (this.pruneTimer !== undefined) return;
      this.pruneTimer = setTimeout(() => {
        this.pruneTimer = undefined;
        this.pruneQueue();
      }, 100);
    });
    this.listen('mapviewchangeend', () => {
      this.observeCamera();
      this.interacting = false;
      this.pruneQueue();
      if (this.dirty) this.scheduleRefresh();
    });
    this.ready = this.initialize().catch((error: unknown) => this.fail(error));
  }

  setOpacity(opacity: number) {
    this.opacity = Math.max(0, Math.min(1, opacity));
    this.provider?.setOpacity(this.opacity);
  }

  diagnostics() {
    return {
      ready: Boolean(this.layer),
      disposed: this.disposed,
      decodedTiles: this.decodedTiles,
      softReloads: this.softReloads,
      attemptedThisView: this.attempted.size,
      ...this.cache.diagnostics(),
    };
  }

  dispose() {
    if (this.disposed) return;
    this.disposed = true;
    clearTimeout(this.refreshTimer);
    clearTimeout(this.pruneTimer);
    this.listeners.forEach(([name, listener]) =>
      this.map.removeEventListener(name, listener),
    );
    if (this.layer) {
      this.map.removeLayer(this.layer);
      this.layer.dispose();
    }
    this.provider?.dispose();
    this.layer = undefined;
    this.provider = undefined;
    this.cache.dispose();
    this.overview.clear();
    this.attempted.clear();
    this.empty.width = this.empty.height = 0;
  }

  private async initialize() {
    const cad = this.metadata.cad!;
    const range = tileRange(cad.bounds, cad.minZoom);
    const count =
      (range.right - range.left + 1) * (range.bottom - range.top + 1);
    if (
      !Number.isInteger(cad.minZoom) ||
      !Number.isInteger(cad.maxZoom) ||
      cad.minZoom < 0 ||
      cad.maxZoom > 24 ||
      cad.minZoom > cad.maxZoom ||
      !Number.isFinite(count) ||
      !Object.values(cad.bounds).every(Number.isFinite) ||
      cad.bounds.north <= cad.bounds.south ||
      cad.bounds.east <= cad.bounds.west ||
      cad.bounds.west < -180 ||
      cad.bounds.east > 180 ||
      !Number.isInteger(Math.log2(cad.tileSize)) ||
      cad.tileSize < 1 ||
      cad.tileSize > 1024 ||
      count < 1 ||
      count > 16 ||
      count * cad.tileSize ** 2 * 4 > 4 * 1024 * 1024
    )
      throw new Error('CAD overview exceeds its supported resource bounds.');
    this.empty.width = this.empty.height = cad.tileSize;
    const requests: Promise<void>[] = [];
    for (let x = range.left; x <= range.right; x += 1) {
      for (let y = range.top; y <= range.bottom; y += 1) {
        const tile = { x, y, z: cad.minZoom };
        requests.push(
          this.cache.request(tile, true).promise.then((canvas) => {
            if (canvas && !this.disposed)
              this.overview.set(`${x}/${y}`, { tile, canvas });
          }),
        );
      }
    }
    await Promise.all(requests);
    if (this.disposed || this.overview.size !== count) return;
    this.provider = new H.map.provider.ImageTileProvider({
      min: 0,
      max: cad.maxZoom,
      tileSize: cad.tileSize,
      opacity: this.opacity,
      getURL: () => undefined,
    });
    this.provider.requestInternal = (x, y, z, onResponse) => {
      if (this.disposed) return { cancel: () => undefined };
      const tile = { x, y, z };
      if (!tilesOverlap(tile, cad.bounds)) {
        onResponse(this.empty);
        return { cancel: () => undefined };
      }
      const ready = this.cache.peek(tile);
      onResponse(ready ?? this.fallback(tile));
      const key = cadTileKey(tile);
      if (
        !this.disposed &&
        !ready &&
        z >= cad.minZoom &&
        !this.attempted.has(key) &&
        this.attempted.size < 512
      ) {
        this.attempted.add(key);
        const lease = this.cache.request(tile);
        return {
          cancel: () => {
            if (lease.cancel()) this.attempted.delete(key);
          },
        };
      }
      return { cancel: () => undefined };
    };
    this.layer = new H.map.layer.TileLayer(this.provider);
    this.map.addLayer(this.layer);
  }

  private scheduleRefresh() {
    if (this.disposed || !this.provider) return;
    this.dirty = true;
    if (this.interacting || this.refreshTimer !== undefined) return;
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = undefined;
      if (this.disposed || !this.provider) return;
      this.dirty = false;
      this.softReloads += 1;
      this.provider.reload(false);
    }, 100);
  }

  private listen(name: string, listener: () => void) {
    this.map.addEventListener(name, listener);
    this.listeners.push([name, listener]);
  }

  private cameraKey() {
    const view = this.map.getViewModel().getLookAtData();
    return JSON.stringify([
      view.position?.lat,
      view.position?.lng,
      view.zoom,
      view.heading,
      view.tilt,
    ]);
  }

  private observeCamera() {
    const key = this.cameraKey();
    if (key === this.viewKey) return;
    this.viewKey = key;
    if (!this.interacting || !this.cameraChanged) this.attempted.clear();
    this.cameraChanged = true;
  }

  private pruneQueue() {
    if (this.disposed) return;
    const view = this.map.getViewModel().getLookAtData();
    const rectangle = view.bounds?.getBoundingBox();
    if (!rectangle) return;
    const bounds = {
      north: rectangle.getTop(),
      south: rectangle.getBottom(),
      west: rectangle.getLeft(),
      east: rectangle.getRight(),
    };
    const cancelled = this.cache.cancelQueuedOutside(
      (tile) =>
        tilesOverlap(tile, bounds) &&
        (view.zoom === undefined || Math.abs(tile.z - view.zoom) <= 2),
    );
    cancelled.forEach((tile) => this.attempted.delete(cadTileKey(tile)));
  }

  private canvas() {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = this.metadata.cad!.tileSize;
    return canvas;
  }

  private fallback(tile: CadTileCoordinate) {
    const canvas = this.canvas();
    const context = canvas.getContext('2d');
    if (!context) return this.empty;
    const size = canvas.width;
    const scale = 2 ** (tile.z - this.metadata.cad!.minZoom);
    for (const source of this.overview.values()) {
      const left = Math.max(source.tile.x, tile.x / scale);
      const top = Math.max(source.tile.y, tile.y / scale);
      const right = Math.min(source.tile.x + 1, (tile.x + 1) / scale);
      const bottom = Math.min(source.tile.y + 1, (tile.y + 1) / scale);
      if (left >= right || top >= bottom) continue;
      context.drawImage(
        source.canvas,
        (left - source.tile.x) * size,
        (top - source.tile.y) * size,
        (right - left) * size,
        (bottom - top) * size,
        (left * scale - tile.x) * size,
        (top * scale - tile.y) * size,
        (right - left) * scale * size,
        (bottom - top) * scale * size,
      );
    }
    return canvas;
  }

  private async decode(tile: CadTileCoordinate, signal: AbortSignal) {
    let bitmap: ImageBitmap | undefined;
    try {
      const blob = await compoundMap.tile(
        this.metadata,
        tile.x,
        tile.y,
        tile.z,
        signal,
      );
      signal.throwIfAborted();
      bitmap = await createImageBitmap(blob);
      signal.throwIfAborted();
      const canvas = this.canvas();
      if (bitmap.width !== canvas.width || bitmap.height !== canvas.height)
        throw new Error('CAD tile dimensions do not match the map.');
      const context = canvas.getContext('2d', {
        willReadFrequently: this.linework,
      });
      if (!context) throw new Error('CAD image processing unavailable.');
      const bounds = this.metadata.cad!.bounds;
      const scale = 2 ** tile.z;
      context.beginPath();
      context.rect(
        (longitudeX(bounds.west) * scale - tile.x) * canvas.width,
        (latitudeY(bounds.north) * scale - tile.y) * canvas.height,
        (longitudeX(bounds.east) - longitudeX(bounds.west)) *
          scale *
          canvas.width,
        (latitudeY(bounds.south) - latitudeY(bounds.north)) *
          scale *
          canvas.height,
      );
      context.clip();
      context.drawImage(bitmap, 0, 0);
      if (this.linework) {
        const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
        removeCadPaper(pixels.data);
        context.putImageData(pixels, 0, 0);
      }
      this.decodedTiles += 1;
      return canvas;
    } catch (error) {
      if (error instanceof CompoundRequestError) throw error;
      if (error instanceof AuthError) throw new CompoundRequestError(401);
      const status = (error as { response?: { status?: number } } | null)
        ?.response?.status;
      if (status && [401, 403, 404, 409].includes(status))
        throw new CompoundRequestError(status);
      throw error;
    } finally {
      bitmap?.close();
    }
  }

  private fail(error: unknown) {
    if (this.disposed) return;
    if (isFatalCadError(error)) this.dispose();
    this.onError(
      error instanceof Error ? error : new Error('CAD request failed.'),
    );
  }
}
